// Rechner: Regal mittig montieren. Interaktive Drag-Vorschau, rastet mittig ein.
import React, { useEffect, useMemo, useState } from "react";
import { calcShelf, fmtCm } from "@/lib/calc";
import { track, trackSettled } from "@/lib/track";
import EditorShell from "./EditorShell";
import { startDrag, round1, clamp, fmt, niceStep, niceCeil } from "@/components/preview/useDragSvg";
import { C, Svg, handle, cross, pill, hDim, vDim } from "@/components/preview/canvasPrimitives";

// Interner State nutzt die calc.js-Schlüssel (height, position, fromLeft).
const DEFAULTS = { wallWidth: "200", shelfWidth: "80", holeSpacing: "60", height: "120", position: "center", fromLeft: "60" };

const W = 880;
const H = 470;
const padL = 64;
const padR = 836;
const wallTopY = 24;
const floorY = 408;

export default function ShelfCalculator() {
  const [v, setV] = useState(DEFAULTS);
  const set = (k) => (val) => setV((s) => ({ ...s, [k]: val }));
  const setNow = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const setMany = (obj) => setV((s) => ({ ...s, ...obj }));
  const num = (k) => {
    const x = parseFloat(String(v[k]).replace(",", "."));
    return isNaN(x) ? NaN : x;
  };

  const r = useMemo(() => calcShelf(v), [v]);

  useEffect(() => {
    if (!r.ok) return;
    trackSettled("calculator_used", { calculator: "shelf_centered", mode: "default" });
    trackSettled("result_shown", { calculator: "shelf_centered", verdict: (r.warnings && r.warnings.length) ? "warn" : "ok" });
  }, [r]);

  const canvas = renderCanvas(v, r, num, setNow, setMany);
  const copyText = r.ok ? buildCopyText(r) : "";

  const fields = [
    { id: "wallWidth", label: "Wandbreite", value: v.wallWidth, step: "1", onChange: set("wallWidth") },
    { id: "shelfWidth", label: "Regalbreite", value: v.shelfWidth, step: "1", onChange: set("shelfWidth") },
    { id: "holeSpacing", label: "Lochabstand", value: v.holeSpacing, step: "0.5", onChange: set("holeSpacing"), hint: "Abstand der beiden Befestigungslöcher." },
    { id: "height", label: "Höhe ab Boden", value: v.height, step: "1", onChange: set("height") },
  ];

  return (
    <EditorShell
      testId="calc-shelf"
      canvas={canvas}
      canvasReady={r.ok}
      emptyHint="Gib Wandbreite, Regalbreite und Lochabstand ein, um die Vorschau zu sehen."
      toolbarHint="Ziehe das Regal (rastet mittig ein), seine Breite, den Lochabstand und die Höhe."
      onCenter={() => setNow("position", "center")}
      onReset={() => setV(DEFAULTS)}
      fields={fields}
      warnings={r.ok ? r.warnings : []}
      hasResult={r.ok}
      results={r.ok ? buildResults(r) : []}
      steps={r.ok ? buildSteps(r) : []}
      copyText={copyText}
    />
  );
}

function renderCanvas(v, r, num, setNow, setMany) {
  const wallWidth = num("wallWidth") || 1;
  const drawW = padR - padL;
  const scaleX = drawW / wallWidth;
  const sx = (cm) => padL + cm * scaleX;
  const xToCm = (px) => (px - padL) / scaleX;
  const sh = num("height") || 0;
  const viewCm = niceCeil(Math.max(sh + 30, 180));
  const scaleY = (floorY - wallTopY) / viewCm;
  const vy = (cm) => floorY - cm * scaleY;
  const yToCm = (px) => (floorY - px) / scaleY;
  const els = [];

  els.push(<rect key="wall" x={padL} y={wallTopY} width={drawW} height={floorY - wallTopY} fill={C.wall} stroke={C.wallStroke} strokeWidth={1.5} rx={4} />);
  const stepV = niceStep(viewCm);
  for (let h = 0; h <= viewCm + 0.1; h += stepV) {
    const y = vy(h);
    els.push(<line key={"lt" + h} x1={padL - 6} y1={y} x2={padL} y2={y} stroke={C.wallStroke} strokeWidth={1} />);
    els.push(
      <text key={"ll" + h} x={padL - 9} y={y + 4} fill={C.dim} fontSize={11} textAnchor="end" fontFamily="Inter,sans-serif" style={{ fontVariantNumeric: "tabular-nums" }}>
        {h}
      </text>
    );
  }
  const stepH = niceStep(wallWidth);
  for (let x = 0; x <= wallWidth + 0.1; x += stepH) {
    const px = sx(x);
    els.push(<line key={"bt" + x} x1={px} y1={floorY} x2={px} y2={floorY + 6} stroke={C.wallStroke} strokeWidth={1} />);
    els.push(
      <text key={"bl" + x} x={px} y={floorY + 20} fill={C.dim} fontSize={11} textAnchor="middle" fontFamily="Inter,sans-serif" style={{ fontVariantNumeric: "tabular-nums" }}>
        {fmt(x)}
      </text>
    );
  }
  els.push(<line key="floor" x1={padL} y1={floorY} x2={padR} y2={floorY} stroke={C.floor} strokeWidth={2.5} />);
  const wcx = sx(wallWidth / 2);
  els.push(<line key="wc" x1={wcx} y1={wallTopY - 4} x2={wcx} y2={floorY} stroke={C.center} strokeWidth={1.4} strokeDasharray="6 5" />);
  els.push(pill("wcl", wcx, wallTopY + 8, "Wandmitte", C.center, "middle", W));

  if (r.ok) {
    const boardY = vy(sh);
    const bx = sx(r.shelfLeft);
    const bw = Math.max(4, r.shelfWidth * scaleX);
    const th = 14;
    const depth = 30;
    const snapped = r.position === "center" || Math.abs(r.shelfCenter - r.wallCenter) < 0.6;
    const scx = sx(r.shelfCenter);
    els.push(<rect key="bsh" x={bx + 5} y={boardY + th / 2 + 1} width={bw} height={9} rx={4} fill="hsl(30 14% 45% / 0.10)" style={{ pointerEvents: "none" }} />);
    [r.hole1, r.hole2].forEach((hx, i) => {
      const X = sx(hx);
      const by = boardY + th / 2;
      els.push(
        <path
          key={"brk" + i}
          d={`M${X - 9} ${by} L${X + 9} ${by} L${X} ${by + depth} Z`}
          fill="hsl(188 72% 28% / 0.16)"
          stroke={C.objectStroke}
          strokeWidth={1.4}
          strokeLinejoin="round"
          style={{ pointerEvents: "none" }}
        />
      );
    });
    els.push(
      <rect
        key="board"
        x={bx}
        y={boardY - th / 2}
        width={bw}
        height={th}
        fill={C.object}
        stroke={C.objectStroke}
        strokeWidth={2}
        rx={3}
        style={{ cursor: "ew-resize" }}
        onPointerDown={(ev) =>
          startDrag(ev, W, H, (cur, st) => {
            const startLeft = r.shelfLeft;
            const nl = clamp(startLeft + (cur.x - st.x) / scaleX, 0, Math.max(0, wallWidth - r.shelfWidth));
            if (Math.abs(nl + r.shelfWidth / 2 - wallWidth / 2) < 5) {
              setNow("position", "center");
            } else {
              setMany({ position: "custom", fromLeft: String(round1(nl)) });
            }
          })
        }
      />
    );
    els.push(<rect key="btop" x={bx + 2} y={boardY - th / 2 + 1.5} width={bw - 4} height={3.5} rx={2} fill="hsl(188 72% 28% / 0.22)" style={{ pointerEvents: "none" }} />);
    els.push(<path key="scm" d={`M${scx} ${boardY - th / 2 - 5} l-5 -7 l10 0 z`} fill={snapped ? C.primary : C.dim} style={{ pointerEvents: "none" }} />);
    [r.hole1, r.hole2].forEach((hx, i) => {
      els.push(<line key={"hg" + i} x1={sx(hx)} y1={boardY} x2={sx(hx)} y2={floorY} stroke={C.guide} strokeWidth={1} strokeDasharray="3 4" />);
    });
    els.push(hDim("h1", padL, sx(r.hole1), floorY - 28, fmt(r.hole1), C.dim, W));
    els.push(hDim("hs", sx(r.hole1), sx(r.hole2), boardY - 30, fmt(r.holeSpacing) + " cm", C.primary, W));
    const vhx = clamp((sx(r.shelfRight) + sx(wallWidth)) / 2, sx(r.shelfRight) + 26, sx(wallWidth) - 30);
    els.push(vDim("vh", floorY, boardY, vhx, "Höhe " + fmt(sh), C.dim, W));
    els.push(cross("hc0", sx(r.hole1), boardY, null));
    els.push(
      cross("hc1", sx(r.hole2), boardY, (ev) =>
        startDrag(ev, W, H, (cur) => setNow("holeSpacing", String(round1(clamp(2 * (xToCm(cur.x) - r.shelfCenter), 2, Math.min(r.shelfWidth, 300))))))
      )
    );
    els.push(
      handle("hw", sx(wallWidth), (wallTopY + floorY) / 2, "ew-resize", (ev) =>
        startDrag(ev, W, H, (cur) => setNow("wallWidth", String(round1(clamp(xToCm(cur.x), 40, 600))))), "v")
    );
    els.push(
      handle("hsw", sx(r.shelfRight), boardY, "ew-resize", (ev) =>
        startDrag(ev, W, H, (cur) => setNow("shelfWidth", String(round1(clamp(2 * (xToCm(cur.x) - r.shelfCenter), 10, 300))))), "v")
    );
    els.push(
      handle("hsh", sx(r.shelfLeft), boardY, "ns-resize", (ev) =>
        startDrag(ev, W, H, (cur) => setNow("height", String(round1(clamp(yToCm(cur.y), 10, viewCm))))), "h")
    );
  }

  return (
    <Svg vbW={W} vbH={H} ariaLabel="Vorschau: Regal mittig montieren">
      {els}
    </Svg>
  );
}

function buildResults(r) {
  const rows = [
    { label: "Bohrloch 1 (von links)", value: fmtCm(r.hole1) },
    { label: "Bohrloch 2 (von links)", value: fmtCm(r.hole2) },
    { label: "Kontrollabstand der Löcher", value: fmtCm(r.controlSpacing) },
    { label: "Regalmitte (von links)", value: fmtCm(r.shelfCenter) },
    { label: "Wandmitte (von links)", value: fmtCm(r.wallCenter) },
  ];
  if (r.height !== null) rows.push({ label: "Höhe ab Boden", value: fmtCm(r.height) });
  return rows;
}

function buildSteps(r) {
  return [
    `Markiere Bohrloch 1 bei ${fmtCm(r.hole1)} und Bohrloch 2 bei ${fmtCm(r.hole2)} von der linken Wandkante.`,
    r.height !== null ? `Übertrage beide Punkte auf ${fmtCm(r.height)} Höhe ab Boden und richte sie mit der Wasserwaage waagerecht aus.` : "Lege die Höhe ab Boden fest und richte beide Punkte mit der Wasserwaage aus.",
    `Kontrolliere den Lochabstand von ${fmtCm(r.controlSpacing)}, dann bohre.`,
    "Achte vor dem Bohren auf Leitungen und die Wandbeschaffenheit.",
  ];
}

function buildCopyText(r) {
  const lines = [
    "Regal mittig montieren",
    `Bohrloch 1 (von links): ${fmtCm(r.hole1)}`,
    `Bohrloch 2 (von links): ${fmtCm(r.hole2)}`,
    `Kontrollabstand: ${fmtCm(r.controlSpacing)}`,
    `Regalmitte: ${fmtCm(r.shelfCenter)}, Wandmitte: ${fmtCm(r.wallCenter)}`,
  ];
  if (r.height !== null) lines.push(`Höhe ab Boden: ${fmtCm(r.height)}`);
  return lines.join("\n");
}
